import { Box, Typography } from '@mui/material';

import Dialog from 'modules/common/components/MUI/Dialog';
import AttentionBanner from 'modules/common/components/AttentionBanner';

interface EligibleStatementsModalProps {
    readonly open: boolean;
    /** Cancel — keep the member on the step so they can still enable. */
    readonly onClose: () => void;
    /** Confirm — proceed with the manual-upload path (skip the step). */
    readonly onConfirm: () => void;
}

/**
 * Shown when the member chooses "I prefer to upload monthly" while they still have eligible
 * (supported, not-yet-enabled) accounts. Explains the manual fallback they're opting into before
 * letting them proceed.
 */
export default function EligibleStatementsModal({ open, onClose, onConfirm }: Readonly<EligibleStatementsModalProps>) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            heading="Automate your bank statements"
            negativeAction={{ label: 'I prefer to upload monthly', onClick: onConfirm }}
            positiveAction={{ label: 'Enable Auto Statements', onClick: onClose }}
            maxWidth="sm"
            fullWidth>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <AttentionBanner
                    variant="warning"
                    description="To keep your books accurate, it is not recommended to skip Auto Statements."
                />
                <Box>
                    <Typography variant="body1" fontWeight="medium" color="text.primary" mb={1}>
                        If you choose to skip:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, m: 0 }}>
                        <Typography component="li" variant="body1" color="text.primary">
                            You&apos;ll need to upload recent statements manually to complete setup.
                        </Typography>
                        <Typography component="li" variant="body1" color="text.primary">
                            You&apos;ll need to upload new statements every month to keep your books updated.
                        </Typography>
                    </Box>
                </Box>
                <Typography variant="body1" color="text.primary">
                    You can manage this anytime from{' '}
                    <Box component="span" fontWeight="bold">
                        Settings → Bank accounts
                    </Box>
                    .
                </Typography>
            </Box>
        </Dialog>
    );
}
